import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Shield, Truck, RotateCcw, CreditCard, Mail, FileText } from 'lucide-react';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      category: 'Spedizioni',
      icon: Truck,
      questions: [
        {
          q: 'Quali sono i tempi di consegna?',
          a: 'I tempi di consegna variano da 7 a 15 giorni lavorativi, a seconda della destinazione. Riceverai un codice tracking via email per monitorare il tuo pacco.'
        },
        {
          q: 'Quanto costa la spedizione?',
          a: 'La spedizione costa €4.99 per ordini sotto €50. Per ordini superiori a €50, la spedizione è GRATUITA!'
        },
        {
          q: 'Spedite in tutta Italia?',
          a: 'Sì, spediamo in tutta Italia, isole comprese. Per spedizioni internazionali, contattaci a postmaster@qantri.store.'
        }
      ]
    },
    {
      category: 'Resi e Rimborsi',
      icon: RotateCcw,
      questions: [
        {
          q: 'Posso restituire un prodotto?',
          a: 'Sì, hai 30 giorni dalla ricezione per restituire un prodotto. Il prodotto deve essere nelle condizioni originali, non usato e con etichette ancora attaccate.'
        },
        {
          q: 'Come faccio un reso?',
          a: 'Contattaci a postmaster@qantri.store con il numero d\'ordine. Ti invieremo le istruzioni per la spedizione del reso. Una volta ricevuto e verificato il prodotto, elaboreremo il rimborso entro 5-7 giorni lavorativi.'
        },
        {
          q: 'Chi paga la spedizione del reso?',
          a: 'Per resi dovuti a difetti o errori nostri, la spedizione è a nostro carico. Per ripensamenti, le spese di reso sono a carico del cliente.'
        }
      ]
    },
    {
      category: 'Pagamenti',
      icon: CreditCard,
      questions: [
        {
          q: 'Quali metodi di pagamento accettate?',
          a: 'Accettiamo pagamenti tramite PayPal (anche con carta di credito/debito attraverso PayPal). I pagamenti sono sicuri e protetti.'
        },
        {
          q: 'Il pagamento è sicuro?',
          a: 'Assolutamente sì. Utilizziamo PayPal, leader mondiale nei pagamenti online, che garantisce la massima sicurezza per le tue transazioni.'
        },
        {
          q: 'Quando viene addebitato il pagamento?',
          a: 'Il pagamento viene addebitato immediatamente al completamento dell\'ordine.'
        }
      ]
    },
    {
      category: 'Privacy e Sicurezza',
      icon: Shield,
      questions: [
        {
          q: 'Come vengono trattati i miei dati?',
          a: 'I tuoi dati sono trattati in conformità con il GDPR. Li utilizziamo esclusivamente per elaborare i tuoi ordini e, se hai dato il consenso, per inviarti comunicazioni promozionali.'
        },
        {
          q: 'Posso cancellare il mio account?',
          a: 'Sì, puoi richiedere la cancellazione del tuo account e di tutti i dati associati contattandoci a postmaster@qantri.store.'
        }
      ]
    }
  ];

  const toggleFAQ = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenIndex(openIndex === key ? null : key);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="faq-page">
      {/* Hero */}
      <section className="bg-[#064E3B] text-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <FileText className="h-12 w-12 mx-auto mb-6 text-[#D4AF37]" />
            <h1 className="text-4xl md:text-5xl font-light mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              FAQ & <span className="italic">Normative</span>
            </h1>
            <p className="text-xl text-white/70">
              Tutto quello che devi sapere su QANTRI
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="py-16 md:py-24 px-6">
        <div className="max-w-4xl mx-auto">
          {faqs.map((section, categoryIndex) => (
            <motion.div
              key={section.category}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#064E3B] rounded-full flex items-center justify-center">
                  <section.icon className="h-5 w-5 text-white" />
                </div>
                <h2 className="text-2xl font-medium">{section.category}</h2>
              </div>
              
              <div className="space-y-3">
                {section.questions.map((faq, questionIndex) => {
                  const isOpen = openIndex === `${categoryIndex}-${questionIndex}`;
                  return (
                    <div
                      key={questionIndex}
                      className="bg-white rounded-xl border border-neutral-200 overflow-hidden"
                    >
                      <button
                        onClick={() => toggleFAQ(categoryIndex, questionIndex)}
                        className="w-full flex items-center justify-between p-5 text-left hover:bg-neutral-50 transition-colors"
                      >
                        <span className="font-medium pr-4">{faq.q}</span>
                        <ChevronDown className={`h-5 w-5 text-neutral-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                      </button>
                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            initial={{ height: 0 }}
                            animate={{ height: 'auto' }}
                            exit={{ height: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="px-5 pb-5 text-neutral-600">
                              {faq.a}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Terms & Privacy Links */}
      <section className="py-16 bg-neutral-100">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl font-medium mb-8 text-center">Documenti Legali</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl text-center">
              <FileText className="h-8 w-8 mx-auto mb-3 text-[#064E3B]" />
              <h3 className="font-medium mb-2">Termini di Servizio</h3>
              <p className="text-sm text-neutral-500">Condizioni generali di vendita</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center">
              <Shield className="h-8 w-8 mx-auto mb-3 text-[#064E3B]" />
              <h3 className="font-medium mb-2">Privacy Policy</h3>
              <p className="text-sm text-neutral-500">Come trattiamo i tuoi dati</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center">
              <RotateCcw className="h-8 w-8 mx-auto mb-3 text-[#064E3B]" />
              <h3 className="font-medium mb-2">Politica Resi</h3>
              <p className="text-sm text-neutral-500">30 giorni per ripensarci</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 text-center px-6">
        <h2 className="text-2xl font-light mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          Non hai trovato la risposta?
        </h2>
        <p className="text-neutral-500 mb-6">Contattaci e ti risponderemo entro 24 ore</p>
        <a 
          href="mailto:postmaster@qantri.store"
          className="inline-flex items-center gap-2 bg-[#064E3B] text-white px-8 py-3 rounded-full hover:bg-[#043927] transition-colors"
        >
          <Mail className="h-4 w-4" />
          postmaster@qantri.store
        </a>
      </section>
    </div>
  );
};

export default FAQPage;
